import { merge } from 'lodash';
import { createSelector, createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import navigationConfig from 'app/fuse-configs/navigationConfig';

import NavigationUtils from '../components/NavigationUtils';

export interface NavigationItem {
	id: string;
	title: string;
	translate: string;
	auth?: string | string[] | null;
	type: 'item' | 'group' | 'collapse';
	icon?: string | false;
	url?: string;
	children?: NavigationItem[];
}

const navigationAdapter = createEntityAdapter<NavigationItem>();
const emptyInitialState = navigationAdapter.getInitialState();
const initialState = navigationAdapter.upsertMany(emptyInitialState, navigationConfig);

export const appendNavigationItem = (item, parentId) => (dispatch, getState) => {
	const navigation = selectNavigationAll(getState());

	return dispatch(setNavigation(NavigationUtils.appendNavItem(navigation, item, parentId)));
};

export const prependNavigationItem = (item, parentId) => (dispatch, getState) => {
	const navigation = selectNavigationAll(getState());

	return dispatch(setNavigation(NavigationUtils.prependNavItem(navigation, item, parentId)));
};

export const updateNavigationItem = (id, item) => (dispatch, getState) => {
	const navigation = selectNavigationAll(getState());

	return dispatch(setNavigation(NavigationUtils.updateNavItem(navigation, id, item)));
};

export const removeNavigationItem = id => (dispatch, getState) => {
	const navigation = selectNavigationAll(getState());

	return dispatch(setNavigation(NavigationUtils.removeNavItem(navigation, id)));
};

export const {
	selectAll: selectNavigationAll,
	selectIds: selectNavigationIds,
	selectById: selectNavigationItemById
} = navigationAdapter.getSelectors((state: any) => state.fuse.navigation);

const navigationSlice = createSlice({
	name: 'navigation',
	initialState,
	reducers: {
		setNavigation: navigationAdapter.setAll,
		resetNavigation: (state, action) => initialState
	}
});

export const { setNavigation, resetNavigation } = navigationSlice.actions;

const getUserRole = ({ user }) => user.roles;

export const selectNavigation = createSelector(
	[
		selectNavigationAll,
		getUserRole,
		({ i18n }) => i18n.language,
		({ user }) => user.app?.routesPerms,
		({ user }) => user.app?.roles,
		({ user }) => user.app?.modules
	],
	(navigation, userRole, language, appRoutesPerms, appRoles, modules) => {
		const filteredNavigation = NavigationUtils.filterDisabledModules(navigation, modules);

		const availableRoutes = NavigationUtils.filterRecursive(filteredNavigation, (item: NavigationItem) => {
			const derivedRoles = NavigationUtils.getDerivedRolesFromAuthRole(item.auth ?? null, appRoles);
			return NavigationUtils.hasPermission(derivedRoles, userRole);
		});

		return NavigationUtils.getTranslatedRoutes(merge([], availableRoutes));
	}
);

export const selectFlatNavigation = createSelector([selectNavigation], navigation =>
	NavigationUtils.getFlatNavigation(navigation)
);

export default navigationSlice.reducer;
